import { z } from 'zod';
import { identifierSchema, matchedOnSchema, paginationQuerySchema, safeText } from './common.js';
import { courseFilterQuerySchema, facetsSchema } from './course.js';
/**
 * Search request and response shapes.
 *
 * Course search reuses the catalog's filter schema verbatim; lesson search adds a chunk-level
 * result that carries a timestamp, which is what makes a deep link into a video possible.
 */
export const searchCoursesQuerySchema = courseFilterQuerySchema.extend({
    ...paginationQuerySchema.shape,
    q: safeText(500),
});
export const lessonSearchQuerySchema = courseFilterQuerySchema.extend({
    ...paginationQuerySchema.shape,
    q: safeText(500),
    courseId: identifierSchema.optional(),
});
/**
 * A highlighted excerpt as **structured data**, never HTML.
 *
 * Snippets are cut from 97 MB of third-party transcript. Returning markup would make every one of
 * those an XSS vector that no amount of React escaping downstream could undo.
 */
export const snippetSchema = z.object({
    text: z.string(),
    highlights: z.array(z.object({ start: z.number().int(), end: z.number().int() })),
});
export const lessonSearchResultSchema = z.object({
    lessonId: z.string(),
    lessonNo: z.number().int(),
    lessonTitle: z.string().nullable(),
    moduleId: z.string(),
    moduleNo: z.number().int(),
    moduleTitle: z.string().nullable(),
    courseId: z.string(),
    courseIsbn: z.string(),
    courseSlug: z.string(),
    courseTitle: z.string(),
    score: z.number(),
    vectorRank: z.number().int().nullable(),
    lexicalRank: z.number().int().nullable(),
    matchedOn: matchedOnSchema,
    matchedChunks: z.number().int(),
    bestChunkId: z.string(),
    /**
     * The matching chunk's own offset, **relative to its segment** — not a deep link.
     *
     * Each `.dfxp` file starts its timeline at zero, so this is a position inside one segment of
     * the lesson. It is here because it is what the chunk row says; `lessonStartMs` is the one to
     * build a link from.
     */
    startMs: z.number().int(),
    endMs: z.number().int(),
    /** Milliseconds into the **lesson**, which is what `?t=` takes. Use this for a deep link. */
    lessonStartMs: z.number().int(),
    snippet: snippetSchema,
});
/**
 * No `total`.
 *
 * Lesson search is ranked retrieval over 28,185 chunks fused from two capped candidate pools;
 * there is no honest count of "all matching lessons" short of a second full pass, and a number
 * that is really "the size of the candidate pool" would be a lie with a plausible shape.
 * `hasMore` is what a paginator actually needs.
 */
export const lessonSearchResponseSchema = z.object({
    items: z.array(lessonSearchResultSchema),
    limit: z.number().int(),
    offset: z.number().int(),
    hasMore: z.boolean(),
    degraded: z.boolean(),
});
export const facetQuerySchema = courseFilterQuerySchema.extend({
    topN: z.coerce.number().int().min(1).max(200).default(25),
});
export const facetsResponseSchema = facetsSchema;
export const suggestQuerySchema = z.object({
    q: safeText(200),
    limit: z.coerce.number().int().min(1).max(10).default(10),
});
export const suggestionSchema = z.object({
    id: z.string(),
    isbn: z.string(),
    slug: z.string(),
    title: z.string(),
    hasTranscripts: z.boolean(),
    score: z.number(),
});
export const suggestResponseSchema = z.object({
    items: z.array(suggestionSchema),
});
//# sourceMappingURL=search.js.map